import { useCallback, useEffect, useRef, useState } from 'react'
import { Disclosure } from '@headlessui/react'
import { Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { sections } from '../content/docs-sections'

/**
 * Public API documentation page. Rendered by LandingApp below the shared
 * Navbar as a self-contained two-column layout: a sticky section-nav on
 * the left (desktop) or a "On this page" disclosure (mobile), and the
 * docs content on the right. Sections come from ../content/docs-sections
 * so the nav auto-derives and every id is deep-linkable (e.g. /docs#match).
 */
export default function DocsPage() {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '')
  // Guards the observer from fighting a click-driven scroll in progress.
  const suppressObserver = useRef(false)

  const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    suppressObserver.current = true
    setActiveId(id)
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    })
    if (history.replaceState) {
      history.replaceState(null, '', `#${id}`)
    } else {
      window.location.hash = id
    }
    // Re-enable observer once the smooth scroll has settled.
    window.setTimeout(() => {
      suppressObserver.current = false
    }, 600)
  }, [])

  // Highlight the section currently nearest the top of the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (suppressObserver.current) return
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      {
        // Trigger when a heading crosses the upper third of the viewport.
        rootMargin: '-96px 0px -70% 0px',
        threshold: 0,
      },
    )
    for (const s of sections) {
      const el = document.getElementById(s.id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [])

  // Honor an incoming deep link (#match-events) on first paint.
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash && sections.some((s) => s.id === hash)) {
      // Defer so layout is ready before we scroll.
      window.setTimeout(() => scrollToSection(hash), 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string,
    close?: () => void,
  ) => {
    e.preventDefault()
    scrollToSection(id)
    close?.()
  }

  const navLinks = (onNavigate?: () => void) => (
    <nav aria-label="On this page">
      <ul className="space-y-1">
        {sections.map((s) => {
          const active = s.id === activeId
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => handleNavClick(e, s.id, onNavigate)}
                aria-current={active ? 'location' : undefined}
                className={`block rounded-md px-3 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dash-bg ${
                  active
                    ? 'bg-dash-accent/10 font-medium text-dash-accent'
                    : 'text-dash-secondary hover:bg-dash-border/40 hover:text-dash-text'
                }`}
              >
                {s.title}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )

  return (
    <div className="min-h-screen bg-dash-bg text-dash-text">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Page heading */}
        <header className="mb-8 border-b border-dash-border pb-8">
          <p className="mb-2 font-mono text-xs uppercase tracking-widest text-dash-accent">
            API Reference
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-dash-text sm:text-4xl">
            Documentation
          </h1>
          <p className="mt-3 max-w-2xl leading-relaxed text-dash-secondary">
            Everything you need to authenticate, match names, and reconcile event feeds with the
            SportsMeld API.
          </p>
        </header>

        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-10">
          {/* Mobile: "On this page" disclosure */}
          <div className="mb-6 lg:hidden">
            <Disclosure>
              {({ open, close }) => (
                <div className="rounded-lg border border-dash-border bg-dash-surface">
                  <Disclosure.Button className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-sm font-medium text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent">
                    <span className="inline-flex items-center gap-2">
                      <Bars3Icon className="h-5 w-5 text-dash-secondary" aria-hidden="true" />
                      On this page
                    </span>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-dash-secondary transition-transform ${
                        open ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="border-t border-dash-border px-2 py-2">
                    {navLinks(close)}
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
          </div>

          {/* Desktop: sticky sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 self-start">
              <p className="mb-3 px-3 font-mono text-xs uppercase tracking-widest text-dash-muted">
                On this page
              </p>
              {navLinks()}
            </div>
          </aside>

          {/* Content */}
          <main className="min-w-0 max-w-3xl">
            {sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                aria-labelledby={`${s.id}-title`}
                className="mb-16 scroll-mt-20"
              >
                <h2
                  id={`${s.id}-title`}
                  className="mb-4 text-2xl font-semibold tracking-tight text-dash-text"
                >
                  {s.title}
                </h2>
                {s.element}
              </section>
            ))}
          </main>
        </div>
      </div>
    </div>
  )
}
